import { fetchRequest, recalculateSessionAsync } from '../../api/api.js';
import { transformCostData } from '../../data-to-ui-adapters/data-transform-functions.js';
import { handleNextStage } from '../../stage-manager.js';
import { getStageCollapseElement, isStageOpen, shouldAutoAdvanceStage } from '../../stage-visibility.js';
import { getCurrentStageId } from '../../state.js';
import { updateSummary } from '../../ui/checkout-summary.js';
import { hasUsableCoordinates } from '../../utils/coordinates.js';

const STYLESHEET_HREF    = 'https://static.sensibleweather.io/js-sdk/v1.10.2/assets/css/fonts.css';
const GUARANTEE_TAG_NAME = 'sensible-weather-guarantee';
const STAGE_ID           = 'sensible_weather';

let hasConfigured = false;
let isMounting = false;
let lastMountedCoverageAmount = null;

export async function init() {
    runWhenSensibleLoaded(() => {
        // Add the sensible weather stylesheet
        addStylesheet();
        attachStageOpenHandler();
        attachHandlers();

        // If a previous stage (eg. unit/date selection) completes after this module has
        // initialised, retry mounting - the booking session may not have been populated yet
        // the first time round.
        document.addEventListener('StageComplete', async () => {
            if (isStageOpen(STAGE_ID))
                await mountQuote();
        });
    });

    // Don't mount on initial app load - the booking session (unit/dates) won't be
    // populated yet. Mounting happens once the stage is actually opened (shown.bs.collapse)
    // or once a previous stage completes (StageComplete) while this stage is visible.
}

function runWhenSensibleLoaded(callback) {
    if (window.Sensible) {
        callback();
        return;
    }

    window.addEventListener('SensibleLoaded', function handleSensibleLoaded() {
        window.removeEventListener('SensibleLoaded', handleSensibleLoaded, false);
        callback();
    }, false);
}

async function mountQuote() {
    if (isMounting)
        return;

    isMounting = true;
    try {
        await recalculateSessionAsync();
        const data = await fetchCredentials();
        if (!data?.keys?.product_id || !data?.keys?.partner_id || parseInt(data?.eligible, 10) !== 1) {
            resetMountedQuote();
            await advanceIfSelectedUnitHasNoLocation();
            return;
        }

        // The booking session (unit/dates) may not be populated yet if this stage is open
        // before stage one has completed - bail out and wait to be retried via StageComplete.
        if (!data.arrival || !data.departure || !hasUsableCoordinates(data.unit?.latitude, data.unit?.longitude)) {
            resetMountedQuote();
            if (data.arrival && data.departure)
                await advanceIfSelectedUnitHasNoLocation();
            return;
        }

        const isOnSandbox = parseInt(data.keys.sandbox_flag, 10) == 1;

        if (!hasConfigured) {
            if (isOnSandbox) Sensible.configure(data.keys.partner_id, data.keys.product_id, 'sandbox');
            else             Sensible.configure(data.keys.partner_id, data.keys.product_id);
            hasConfigured = true;
        }

        // The widget caches its quote/selection state once mounted, so if the booking total
        // has changed since the last quote was generated, throw away the mounted component
        // and start again to force a fresh quote against the new total.
        if (lastMountedCoverageAmount !== null && lastMountedCoverageAmount !== data.total_cost)
            recreateGuaranteeElement();

        const sensibleSettings = {
            coverageStartDate          : data.arrival,
            coverageEndDate            : data.departure,
            currency                   : 'gbp',
            exposureName               : data.unit.name,
            exposureLatitude           : parseFloat(data.unit.latitude),
            exposureLongitude          : parseFloat(data.unit.longitude),
            exposureTotalCoverageAmount: data.total_cost,
            externalId                 : data.session_id,
            langLocale                 : 'en-us'
        };

        Sensible.mountComponent(sensibleSettings);
        lastMountedCoverageAmount = data.total_cost;
    } finally {
        isMounting = false;
    }
}

function recreateGuaranteeElement() {
    const existingEl = document.querySelector(GUARANTEE_TAG_NAME);
    if (!existingEl)
        return;

    const freshEl = document.createElement(GUARANTEE_TAG_NAME);
    existingEl.replaceWith(freshEl);
}

function resetMountedQuote() {
    recreateGuaranteeElement();
    lastMountedCoverageAmount = null;
}

async function fetchCredentials() {
    try {
        const response = await fetchRequest('/sensible_weather/initialise/');
        return response.json();
    } catch (err) {
        console.error(err);
    }
}

function addStylesheet() {
    if (document.querySelector(`link[href="${STYLESHEET_HREF}"]`))
        return;

    const styleSheetLinkEl = document.createElement('link');
    
    styleSheetLinkEl.rel = "stylesheet";
    styleSheetLinkEl.href = STYLESHEET_HREF;

    document.head.append(styleSheetLinkEl);
}

function attachStageOpenHandler() {
    const sensibleWeatherStageEl = getStageCollapseElement(STAGE_ID);
    if (!sensibleWeatherStageEl)
        return;

    let hasOpenedBefore = false;

    sensibleWeatherStageEl.addEventListener('shown.bs.collapse', async () => {
        // If the guest goes back to a previous stage and forward again, throw away the
        // mounted widget and start fresh - it should never reuse a cached quote/selection.
        if (hasOpenedBefore) {
            recreateGuaranteeElement();
            lastMountedCoverageAmount = null;
        }
        hasOpenedBefore = true;

        await mountQuote();
    });
}

async function advanceIfSelectedUnitHasNoLocation() {
    if (getCurrentStageId() !== STAGE_ID || !isStageOpen(STAGE_ID))
        return;

    if (shouldAutoAdvanceStage(STAGE_ID))
        await handleNextStage(STAGE_ID);
}

async function attachHandlers() {
    Sensible.setCreateGuaranteeCallback(async (quoteObj) => createGuarantee(quoteObj));
    Sensible.setSelectGuaranteeCallback(async (quoteObj) => setOption(true, quoteObj));
    Sensible.setUnselectGuaranteeCallback(async (quoteObj) => setOption(false, quoteObj));
}

async function createGuarantee(quoteData) {
    await fetchRequest('/sensible_weather/quoteGenerated', buildQuoteBody(quoteData));
    await setOption(false, quoteData);
}

function buildQuoteBody(quoteObj) {
    return {
        quote_id  : quoteObj?.quoteData?.id,
        quote_data: JSON.stringify(quoteObj),
        premium   : parseFloat(quoteObj?.quoteData.totalPrice)
    }
}

async function setOption(doSelectGuarantee, quoteObj) {
    const body = buildQuoteBody(quoteObj);

    const endpoint = doSelectGuarantee ? 'yesChoice' : 'noChoice';
    const response = await fetchRequest(`/sensible_weather/${endpoint}`, body);

    if (response.status == 200) {
        const calcResponse = await recalculateSessionAsync();
        if (calcResponse?.booking_info)
            updateSummary(transformCostData(calcResponse.booking_info));
    }
}
